import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useClients } from '../../hooks/useClients';
import ClientSelect from './ClientSelect';
import ClientAddForm from './ClientAddForm';

export default function ClientSelectionModal({ open, onClose, onClientConfirmed, prefillClient = null }) {
  const [clientMode, setClientMode] = useState('select');
  /** When true, Add form saves by merging into prefill (edit). Cleared if user switches to Find / Select. */
  const [allowMergeFromEdit, setAllowMergeFromEdit] = useState(false);
  const { clients, loading, error, fetchClients, addClient } = useClients();

  useEffect(() => {
    if (!open) return;
    if (prefillClient) {
      setClientMode('add');
      setAllowMergeFromEdit(true);
    } else {
      setClientMode('select');
      setAllowMergeFromEdit(false);
    }
  }, [open, prefillClient]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleProceed = useCallback(
    (client) => {
      onClientConfirmed?.(client);
      handleClose();
    },
    [onClientConfirmed, handleClose]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography component="span" variant="h6" fontWeight={600}>
          {prefillClient ? 'Edit client for proposal' : 'Select client for proposal'}
        </Typography>
        <IconButton edge="end" onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          maxHeight: { xs: '85vh', sm: '80vh' },
          overflowY: 'auto',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose an existing client or add a new one before building the quotation.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card
            variant="outlined"
            sx={{
              borderColor: clientMode === 'select' ? 'primary.main' : 'divider',
              borderWidth: clientMode === 'select' ? 2 : 1,
              transition: 'border-color 0.2s ease',
            }}
          >
            <CardActionArea
              onClick={() => {
                setClientMode('select');
                setAllowMergeFromEdit(false);
              }}
              sx={{ py: 1 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonSearchIcon color={clientMode === 'select' ? 'primary' : 'action'} sx={{ fontSize: 36, mb: 0.5 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Find / Select client
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pick from saved clients
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
          <Card
            variant="outlined"
            sx={{
              borderColor: clientMode === 'add' ? 'primary.main' : 'divider',
              borderWidth: clientMode === 'add' ? 2 : 1,
              transition: 'border-color 0.2s ease',
            }}
          >
            <CardActionArea onClick={() => setClientMode('add')} sx={{ py: 1 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonAddIcon color={clientMode === 'add' ? 'primary' : 'action'} sx={{ fontSize: 36, mb: 0.5 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Add new client
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Register then continue
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>

        {clientMode === 'select' ? (
          <ClientSelect
            clients={clients}
            loading={loading}
            error={error}
            fetchClients={fetchClients}
            onProceed={handleProceed}
          />
        ) : (
          <ClientAddForm
            open={open}
            addClient={addClient}
            onProceed={handleProceed}
            initialValues={allowMergeFromEdit ? prefillClient : null}
            existingClient={allowMergeFromEdit && prefillClient ? prefillClient : null}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
